import Tesseract from "tesseract.js";
import { DocumentType } from "./types";
import { CONFIG } from "./config";
import { COUNTRIES } from "./countries";
import { analyzeForensics } from "./forensicAnalyzer";
import jsQR from "jsqr";

/**
 * Enterprise-Grade Local Forensic Engine (V8)
 * Fixed false positives for Student/College IDs using negative keyword filters.
 */

async function preprocessImage(dataUrl, contrast) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const factor =
        (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));

      for (let i = 0; i < data.length; i += 4) {
        const gray =
          0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        const val = factor * (gray - 128) + 128;
        data[i] = data[i + 1] = data[i + 2] = Math.max(0, Math.min(255, val));
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.src = dataUrl;
  });
}

export async function verifyDualSidedLocally(
  frontImg,
  backImg,
  expectedType,
  expectedCountryName
) {
  try {
    const contrast = CONFIG.engine.contrastLevel;

    const [frontProcessed, backProcessed, forensicReport] = await Promise.all([
      preprocessImage(frontImg, contrast),
      preprocessImage(backImg, contrast),
      analyzeForensics(frontImg),
    ]);

    const initialWorker = await Tesseract.createWorker(
      CONFIG.engine.defaultLanguage
    );
    const {
      data: { text: prelimText },
    } = await initialWorker.recognize(frontProcessed);
    await initialWorker.terminate();

    const normalizedText = prelimText.toUpperCase();

    // Identify Country Profile
    let profile =
      COUNTRIES.find(
        (c) =>
          c.name.toLowerCase() === expectedCountryName.toLowerCase() ||
          c.frontKeywords.some((k) => normalizedText.includes(k))
      ) || COUNTRIES.find((c) => c.id === CONFIG.engine.fallbackProfile);

    // High-Fidelity OCR
    const worker = await Tesseract.createWorker(profile.languages);
    const [frontRes, backRes] = await Promise.all([
      worker.recognize(frontProcessed),
      worker.recognize(backProcessed),
    ]);
    await worker.terminate();

    const frontText = frontRes.data.text.toUpperCase();
    const backText = backRes.data.text.toUpperCase();
    const fullText = frontText + " " + backText;
    const avgConfidence =
      (frontRes.data.confidence + backRes.data.confidence) / 2;

    // 1. Negative Keyword Filter (CRITICAL: Prevents College/Student IDs)
    const foundNegative = profile.negativeKeywords.find((k) =>
      fullText.includes(k)
    );

    // 2. Pattern Match
    const docNumRegex = new RegExp(profile.docNumRegex, "g");
    const hasDocNum = docNumRegex.test(fullText);

    let detectedType = DocumentType.ID_CARD;
    if (frontText.includes("PASSPORT")) detectedType = DocumentType.PASSPORT;
    if (frontText.includes("DRIVING") || frontText.includes("LICENSE"))
      detectedType = DocumentType.DRIVING_LICENSE;

    // 3. Final Decision Logic
    const countryMatches = profile.name
      .toLowerCase()
      .includes(expectedCountryName.toLowerCase().split(" ")[0]);
    const typeMatches = detectedType === expectedType;
    const hasRequiredKeyword = profile.frontKeywords.some((k) =>
      frontText.includes(k)
    );

    // Reject if negative keywords found or missing core ID keywords
    const isValid =
      !foundNegative &&
      countryMatches &&
      typeMatches &&
      (hasDocNum || hasRequiredKeyword) &&
      avgConfidence > CONFIG.thresholds.minOcrConfidence;

    let narrative = "";
    if (isValid) {
      narrative = `Local analysis confirms a physical government-issued ${detectedType.replace(
        "_",
        " "
      )} from ${profile.name}. `;
      if (forensicReport.humanPresenceDetected) narrative += "Hands detected. ";
      narrative += "Security markers validated.";
    } else {
      if (foundNegative) {
        narrative = `Rejected: This appears to be a ${foundNegative} card, not a National ID. Verification failed.`;
      } else if (!countryMatches) {
        narrative = `Region mismatch. Document belongs to ${profile.name}.`;
      } else {
        narrative =
          "Verification failed. The card does not exhibit required government security markers or is too blurry.";
      }
    }

    const extraction = extractExtensiveData(frontText, backText, profile);

    console.log("extraction", extraction);

    return {
      isValid,
      detectedType,
      detectedCountry: profile.name,
      confidence: avgConfidence / 100,
      reasoning: narrative,
      isPhysicalDocument:
        forensicReport.livenessScore >
        CONFIG.thresholds.physicalDocumentConfidence,
      forensics: forensicReport,
      extractedInfo: extraction,
    };
  } catch (error) {
    console.error("Local Engine Error:", error);
    throw error;
  }
}
export async function verifySingleSideLocally(
  frontImg,
  expectedType,
  expectedCountryName
) {
  let frontImgUrl = frontImg;
  const isBlob = frontImg instanceof Blob || frontImg instanceof File;

  if (isBlob) {
    frontImgUrl = URL.createObjectURL(frontImg);
  }

  try {
    const contrast = CONFIG.engine.contrastLevel;

    const [frontProcessed, forensicReport] = await Promise.all([
      preprocessImage(frontImgUrl, contrast),
      analyzeForensics(frontImgUrl),
    ]);

    const initialWorker = await Tesseract.createWorker(
      CONFIG.engine.defaultLanguage
    );
    const {
      data: { text: prelimText },
    } = await initialWorker.recognize(frontProcessed);
    await initialWorker.terminate();

    console.log("prelimText", prelimText);

    const normalizedText = prelimText.toUpperCase();

    console.log("normalizedText", normalizedText);

    // Identify Country Profile
    let profile =
      COUNTRIES.find(
        (c) =>
          c.name.toLowerCase() === expectedCountryName.toLowerCase() ||
          c.frontKeywords.some((k) => normalizedText.includes(k))
      ) || COUNTRIES.find((c) => c.id === CONFIG.engine.fallbackProfile);

    // High-Fidelity OCR
    const worker = await Tesseract.createWorker(profile.languages);
    const frontRes = await worker.recognize(frontProcessed);
    await worker.terminate();

    const frontText = frontRes.data.text.toUpperCase();

    console.log("frontText", frontText);

    const avgConfidence = frontRes.data.confidence;

    // Negative Keyword Filter
    const foundNegative = profile.negativeKeywords.find((k) =>
      frontText.includes(k)
    );

    // Pattern Match
    const docNumRegex = new RegExp(profile.docNumRegex, "g");
    const hasDocNum = docNumRegex.test(frontText);

    let detectedType = DocumentType.ID_CARD;
    if (frontText.includes("PASSPORT")) detectedType = DocumentType.PASSPORT;
    if (frontText.includes("DRIVING") || frontText.includes("LICENSE"))
      detectedType = DocumentType.DRIVING_LICENSE;

    const countryMatches = profile.name
      .toLowerCase()
      .includes(expectedCountryName.toLowerCase().split(" ")[0]);
    const typeMatches = detectedType === expectedType;
    const hasRequiredKeyword = profile.frontKeywords.some((k) =>
      frontText.includes(k)
    );

    const isValid =
      !foundNegative &&
      countryMatches &&
      typeMatches &&
      (hasDocNum || hasRequiredKeyword) &&
      avgConfidence > CONFIG.thresholds.minOcrConfidence;

    let narrative = "";
    if (isValid) {
      narrative = `Local analysis confirms a physical government-issued ${detectedType.replace(
        "_",
        " "
      )} from ${profile.name}. `;
      if (forensicReport.humanPresenceDetected) narrative += "Hands detected. ";
      narrative += "Security markers validated.";
    } else {
      if (foundNegative) {
        narrative = `Rejected: This appears to be a ${foundNegative} card, not a National ID. Verification failed.`;
      } else if (!countryMatches) {
        narrative = `Region mismatch. Document belongs to ${profile.name}.`;
      } else {
        narrative =
          "Verification failed. The card does not exhibit required government security markers or is too blurry.";
      }
    }

    const extraction = extractExtensiveData(frontText, "", profile);

    return {
      isValid,
      detectedType,
      typeMatches,
      detectedCountry: profile.name,
      confidence: avgConfidence / 100,
      reasoning: narrative,
      isPhysicalDocument:
        forensicReport.livenessScore >
        CONFIG.thresholds.physicalDocumentConfidence,
      forensics: forensicReport,
      extractedInfo: extraction,
    };
  } catch (error) {
    console.error("Local Engine Error:", error);
    throw error;
  } finally {
    if (isBlob) {
      URL.revokeObjectURL(frontImgUrl);
    }
  }
}

function extractExtensiveData(front, back, profile) {
  const fullText = front + " " + back;
  const lines = front
    .split("\n")
    .concat(back.split("\n"))
    .map((l) => l.trim().toUpperCase());
  const res = {
    name: "",
    documentNumber: "",
    expiryDate: "",
    dob: "",
    address: "",
  };

  const docNumRegex = new RegExp(profile.docNumRegex);
  const docMatch = fullText.match(docNumRegex);
  if (docMatch) res.documentNumber = docMatch[0];

  const forbidden = [
    "NADRA",
    "GOVERNMENT",
    "PAKISTAN",
    "IDENTITY",
    "CARD",
    "CNIC",
    "STUDENT",
    "UNIVERSITY",
    "COLLEGE",
    "PASSPORT",
    "LICENSE",
    "USA",
  ];
  const nameCandidates = lines.slice(0, 15).filter((l) => {
    const words = l.split(" ").filter((w) => w.length > 2);
    return (
      words.length >= 2 &&
      words.length <= 4 &&
      !/\d/.test(l) &&
      !forbidden.some((f) => l.includes(f))
    );
  });
  if (nameCandidates.length > 0) res.name = nameCandidates[0];

  return res;
}

export async function verifyDocumentLocally(
  imageData,
  expectedType,
  expectedCountry
) {
  return verifyDualSidedLocally(
    imageData,
    imageData,
    expectedType,
    expectedCountry
  );
}

export const identifyDocumentFromBlob = async (blob) => {
  // 1. Initialize the worker with multiple languages (common for KYC)
  const worker = await Tesseract.createWorker(["eng", "fra", "ara"]);

  try {
    // 2. Tesseract.js accepts Blobs directly
    const {
      data: { text },
    } = await worker.recognize(blob);
    const content = text.toLowerCase();

    // 3. Define the identifier logic
    const patterns = {
      PASSPORT: ["passport", "passeport", "p<", "issuing state", "nationality"],
      DRIVING_LICENSE: [
        "driving license",
        "permis de conduire",
        "licence no",
        "categories",
      ],
      ID_CARD: [
        "identity card",
        "carte nationale",
        "national id",
        "birth date",
        "id no",
      ],
    };

    // 4. Match patterns
    let detectedType = "UNKNOWN";

    for (const [docType, keywords] of Object.entries(patterns)) {
      if (keywords.some((word) => content.includes(word))) {
        detectedType = docType;
        break;
      }
    }

    return {
      success: detectedType === "UNKNOWN" ? false : true,
      documentType: detectedType,
      confidence: detectedType !== "UNKNOWN" ? "High" : "Low",
    };
  } catch (error) {
    console.error("OCR Processing Error:", error);
    return { success: false, error: error.message };
  } finally {
    // 5. Always terminate to free up memory (especially in Docker/Node)
    await worker.terminate();
  }
};

// You'll need to install: npm install jsqr

/**
 * Detects if the image is a document based on the side being scanned.
 * @param {Blob|File|string} imgBlob
 * @param {'front' | 'back'} side
 */
export async function detectIfDocument(imgBlob, side = "front") {
  let imgUrl = "";
  const isBlob = imgBlob instanceof Blob || imgBlob instanceof File;

  if (!imgBlob) return { isDocument: false, reason: "No image provided" };

  try {
    imgUrl = isBlob ? URL.createObjectURL(imgBlob) : imgBlob;

    // 1. QR Code Detection
    const qrData = await scanQRCode(imgUrl);
    const hasQRCode = qrData !== null;

    // 2. Forensic Check
    const forensics = await analyzeForensics(imgUrl);

    // 3. OCR Pass
    const processedImg = await preprocessImage(
      imgUrl,
      CONFIG.engine.contrastLevel
    );
    const worker = await Tesseract.createWorker(["eng"]);
    const {
      data: { text, confidence },
    } = await worker.recognize(processedImg);
    await worker.terminate();

    const normalizedText = text.toUpperCase();

    // 4. Detection Triggers
    const hasMRZ =
      normalizedText.includes("<<") ||
      (normalizedText.match(/<(?=.*<)/g) || []).length > 5;
    const docKeywords = [
      "PASSPORT",
      "IDENTITY",
      "DRIVING",
      "LICENSE",
      "NATIONAL",
      "CARD",
      "RESIDENCE",
    ];
    const hasKeywords = docKeywords.some((k) => normalizedText.includes(k));

    // 5. Side-Specific Decision Logic
    let isDocument = false;

    if (side === "back") {
      // For the back side, the QR code is the main proof
      // We also allow MRZ because many European/Asian IDs have the MRZ on the back
      isDocument = hasQRCode || hasMRZ;
    } else {
      // Front side logic (original)
      isDocument =
        hasKeywords && confidence > CONFIG.thresholds.minOcrConfidence;
    }

    return {
      isDocument,
      side,
      hasQRCode,
      qrContent: qrData,
      detectedType: getDocType(normalizedText, hasKeywords),
      confidence: confidence / 100,
      hasMRZ,
      forensics,
      reason: isDocument
        ? `${side.toUpperCase()} side markers detected`
        : `No identifiers found for ${side} side`,
    };
  } catch (error) {
    console.error("Detection Error:", error);
    return { isDocument: false, reason: "Processing error" };
  } finally {
    if (isBlob) URL.revokeObjectURL(imgUrl);
  }
}

/**
 * Helper to scan QR codes from the image URL/Blob
 */
async function scanQRCode(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      context.drawImage(img, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      resolve(code ? code.data : null);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function getDocType(text, hasKeywords) {
  // 1. Check for Passport (Highest priority)
  if (
    text.includes("PASSPORT") ||
    text.includes("PASSEPORT") ||
    text.includes("P<")
  ) {
    return "PASSPORT";
  }

  // 2. Check for Driving License
  const licenseKeywords = [
    "DRIVING",
    "LICENSE",
    "LICENCE",
    "PERMIS",
    "CONDUIRE",
    "DL",
    "IDENTITY NUMBER",
  ];
  if (licenseKeywords.some((k) => text.includes(k))) {
    return "DRIVING_LICENSE";
  }

  // 3. Check for Identity Card (Specific variations)
  const idKeywords = [
    "IDENTITY",
    "IDENTITE",
    "ID CARD",
    "NATIONAL ID",
    "CNIC",
    "NID",
    "IDENTITY NUMBER",
    "DATE",
    "BIRTH",
  ];
  if (idKeywords.some((k) => text.includes(k))) {
    return "ID_CARD";
  }

  // 4. Fallback for MRZ or general keywords
  if (text.includes("<<<<") || hasKeywords) {
    return "GOVERNMENT_DOCUMENT";
  }

  return "UNKNOWN";
}

export async function startAutoCapture(
  videoElement,
  onCapture,
  options = { side: "front", interval: 1000 }
) {
  let isProcessing = false;
  let stopRequested = false;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  const checkFrame = async () => {
    if (stopRequested) return;

    // Only process if video is playing and not currently busy with a previous OCR task
    if (
      !isProcessing &&
      videoElement.readyState === videoElement.HAVE_ENOUGH_DATA
    ) {
      isProcessing = true;

      console.log("Processing frame");

      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async (blob) => {
          try {
            // Uses your existing detectIfDocument logic
            const result = await detectIfDocument(blob, options.side);

            // If a document is detected based on your engine's logic
            if (result.isDocument) {
              stopRequested = true;
              onCapture(blob);
            }
          } catch (err) {
            console.error("Auto-capture processing error:", err);
          } finally {
            isProcessing = false;
          }
        },
        "image/jpeg",
        0.95
      );
    }

    // Interval to prevent CPU over-usage
    if (!stopRequested) {
      setTimeout(checkFrame, options.interval);
    }
  };

  checkFrame();

  // Return cleanup function to stop the loop
  return () => {
    stopRequested = true;
  };
}
